// pages/Services.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  Globe, 
  ShoppingCart, 
  Film, 
  Palette, 
  BarChart, 
  Layout, 
  Server, 
  Smartphone,
  CheckCircle,
  Clock,
  Users,
  Shield,
  Zap,
  ChevronRight,
  ArrowRight,
  Search,
  Filter
} from 'lucide-react';

const Services = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  const services = [
    {
      id: 1,
      icon: <Code />,
      title: 'Web Development',
      category: 'development',
      description: 'Custom web applications built with modern technologies for optimal performance and scalability.',
      features: [
        'Responsive Design',
        'Cross-browser Compatibility',
        'SEO Optimization',
        'Performance Optimization',
        'Maintenance & Support'
      ],
      duration: '2-8 weeks',
      projects: '150+',
      color: 'blue'
    },
    {
      id: 2,
      icon: <Globe />,
      title: 'WordPress Development',
      category: 'development',
      description: 'Professional WordPress websites with custom themes, plugins, and complete CMS solutions.',
      features: [
        'Custom Theme Development',
        'Plugin Development',
        'WooCommerce Integration',
        'Security Optimization',
        'Regular Updates'
      ],
      duration: '1-4 weeks',
      projects: '120+',
      color: 'green'
    },
    {
      id: 3,
      icon: <Layout />,
      title: 'Frontend Development',
      category: 'development',
      description: 'Interactive user interfaces using React, Vue, Angular with modern frameworks and libraries.',
      features: [
        'React/Vue/Angular',
        'TypeScript Integration',
        'State Management',
        'Component Libraries',
        'Performance Tuning'
      ],
      duration: '1-6 weeks',
      projects: '180+',
      color: 'purple'
    },
    {
      id: 4,
      icon: <Server />,
      title: 'Backend Development',
      category: 'development',
      description: 'Robust server-side solutions with Node.js, Python, PHP, and database architecture.',
      features: [
        'API Development',
        'Database Design',
        'Server Configuration',
        'Security Implementation',
        'Cloud Integration'
      ],
      duration: '2-8 weeks',
      projects: '130+',
      color: 'red'
    },
    {
      id: 5,
      icon: <Smartphone />,
      title: 'Full Stack Development',
      category: 'development',
      description: 'End-to-end web solutions covering both frontend and backend development.',
      features: [
        'MERN/MEAN Stack',
        'Real-time Applications',
        'Microservices',
        'DevOps Integration',
        'Full Lifecycle Support'
      ],
      duration: '4-12 weeks',
      projects: '90+',
      color: 'teal'
    },
    {
      id: 6,
      icon: <ShoppingCart />,
      title: 'E-commerce Solutions',
      category: 'business',
      description: 'Complete online store solutions with secure payment integrations and inventory management.',
      features: [
        'Shopify/WooCommerce',
        'Payment Gateway Setup',
        'Inventory Management',
        'Order Tracking',
        'Analytics Dashboard'
      ],
      duration: '3-10 weeks',
      projects: '75+',
      color: 'yellow'
    },
    {
      id: 7,
      icon: <BarChart />,
      title: 'Digital Marketing',
      category: 'marketing',
      description: 'Comprehensive marketing strategies to boost your online presence and drive conversions.',
      features: [
        'SEO Optimization',
        'Social Media Marketing',
        'PPC Campaigns',
        'Content Strategy',
        'Analytics & Reporting'
      ],
      duration: 'Ongoing',
      projects: '60+',
      color: 'pink'
    },
    {
      id: 8,
      icon: <Film />,
      title: 'Video Editing',
      category: 'creative',
      description: 'Professional video editing services for marketing, promotional, and corporate content.',
      features: [
        '4K Video Editing',
        'Motion Graphics',
        'Color Grading',
        'Audio Enhancement',
        'Multiple Format Export'
      ],
      duration: '1-3 weeks',
      projects: '45+',
      color: 'indigo'
    },
    {
      id: 9,
      icon: <Palette />,
      title: 'Graphic Designing',
      category: 'creative',
      description: 'Creative design solutions for branding, marketing materials, and visual communication.',
      features: [
        'Logo Design',
        'Brand Identity',
        'Print Materials',
        'UI/UX Design',
        'Social Media Graphics'
      ],
      duration: '1-4 weeks',
      projects: '110+',
      color: 'orange'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Services', count: services.length },
    { id: 'development', label: 'Development', count: services.filter(s => s.category === 'development').length },
    { id: 'business', label: 'Business Solutions', count: services.filter(s => s.category === 'business').length },
    { id: 'marketing', label: 'Marketing', count: services.filter(s => s.category === 'marketing').length },
    { id: 'creative', label: 'Creative Services', count: services.filter(s => s.category === 'creative').length }
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    green: 'bg-green-100 text-green-600 border-green-200',
    purple: 'bg-purple-100 text-purple-600 border-purple-200',
    red: 'bg-red-100 text-red-600 border-red-200',
    teal: 'bg-teal-100 text-teal-600 border-teal-200',
    yellow: 'bg-yellow-100 text-yellow-600 border-yellow-200',
    pink: 'bg-pink-100 text-pink-600 border-pink-200',
    indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    orange: 'bg-orange-100 text-orange-600 border-orange-200'
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.features.some(f => f.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filter === 'all' || service.category === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our <span className="text-blue-200">Services</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Comprehensive technology solutions to transform your business and drive growth.
              From development to marketing, we've got you covered.
            </p>
            <div className="relative max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search services by name or feature..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <Filter className="text-gray-600" />
              <span className="font-medium text-gray-700">Filter by:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setFilter(category.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    filter === category.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.label}
                  <span className="ml-2 text-sm opacity-75">
                    ({category.count})
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                {/* Service Header */}
                <div className={`p-6 ${colorClasses[service.color]} border-b`}>
                  <div className="flex items-start justify-between">
                    <div className={`w-14 h-14 rounded-xl ${colorClasses[service.color].split(' ')[0]} flex items-center justify-center`}>
                      <div className="w-8 h-8">
                        {service.icon}
                      </div>
                    </div>
                    <span className="text-sm font-semibold px-3 py-1 bg-white/50 rounded-full">
                      {service.category.charAt(0).toUpperCase() + service.category.slice(1)}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold mt-4">{service.title}</h3>
                  <p className="mt-2 opacity-90">{service.description}</p>
                </div>

                {/* Service Details */}
                <div className="p-6">
                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      Key Features
                    </h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="flex items-center text-gray-600"
                        >
                          <ChevronRight className="w-4 h-4 mr-2 text-blue-500" />
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm text-gray-600">Duration</span>
                      </div>
                      <div className="font-semibold text-gray-900 mt-1">{service.duration}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm text-gray-600">Projects</span>
                      </div>
                      <div className="font-semibold text-gray-900 mt-1">{service.projects}</div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center"
                  >
                    Get This Service
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* No Results */}
          {filteredServices.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No services found</h3>
              <p className="text-gray-600">
                Try adjusting your search or filter to find what you're looking for.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilter('all');
                }}
                className="mt-6 text-blue-600 font-semibold hover:text-blue-700"
              >
                Clear filters
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our <span className="text-blue-400">Process</span>
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              We follow a systematic approach to ensure quality and timely delivery of every project.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery & Planning',
                description: 'We analyze requirements and create a detailed project plan.'
              },
              {
                step: '02',
                title: 'Design & Development',
                description: 'Our team designs and develops the solution with regular updates.'
              },
              {
                step: '03',
                title: 'Testing & Quality',
                description: 'Rigorous testing ensures bug-free and high-quality delivery.'
              },
              {
                step: '04',
                title: 'Launch & Support',
                description: 'Deployment and ongoing support for your peace of mind.'
              }
            ].map((process, index) => (
              <motion.div
                key={process.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-blue-300 mb-4">{process.step}</div>
                  <h3 className="text-xl font-semibold mb-3">{process.title}</h3>
                  <p className="text-gray-300">{process.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 right-0 w-8 h-0.5 bg-blue-400 transform translate-x-4 -translate-y-1/2" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Flexible <span className="text-blue-600">Pricing</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose from our flexible pricing models that suit your project requirements and budget.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'Basic',
                price: '$999',
                description: 'Perfect for small projects and startups',
                features: [
                  'Up to 5 pages',
                  'Basic Design',
                  '3 Revisions',
                  'Mobile Responsive',
                  '1 Month Support'
                ],
                popular: false,
                color: 'gray'
              },
              {
                name: 'Professional',
                price: '$2,999',
                description: 'Ideal for growing businesses',
                features: [
                  'Up to 15 pages',
                  'Custom Design',
                  'Unlimited Revisions',
                  'E-commerce Ready',
                  '3 Months Support',
                  'SEO Optimization',
                  'Analytics Setup'
                ],
                popular: true,
                color: 'blue'
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                description: 'For large-scale projects',
                features: [
                  'Unlimited Pages',
                  'Advanced Features',
                  'Priority Support',
                  'Dedicated Team',
                  '6 Months Support',
                  'Advanced Security',
                  'Custom Integrations',
                  'Training Sessions'
                ],
                popular: false,
                color: 'purple'
              }
            ].map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className={`rounded-2xl p-8 border-2 ${
                  plan.popular
                    ? 'border-blue-500 shadow-xl relative'
                    : 'border-gray-200 shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-2">{plan.price}</div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Let's discuss your project and find the perfect solution for your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Start Free Consultation
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition"
              >
                View Our Portfolio
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-blue-600">Questions</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about our services and process.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {[
              {
                question: 'What is your typical project timeline?',
                answer: 'Project timelines vary based on complexity. Small websites take 2-4 weeks, while complex applications can take 8-12 weeks. We provide detailed timelines during the planning phase.'
              },
              {
                question: 'Do you provide ongoing maintenance?',
                answer: 'Yes, we offer maintenance packages starting from $99/month. This includes updates, security patches, backups, and technical support.'
              },
              {
                question: 'Can you work with existing designs?',
                answer: 'Absolutely! We can work with your existing designs or improve them. We also offer redesign services if needed.'
              },
              {
                question: 'What technologies do you use?',
                answer: 'We use modern technologies including React, Vue, Node.js, Python, WordPress, and various cloud platforms. We choose the best stack for each project.'
              },
              {
                question: 'How do you handle project communication?',
                answer: 'We use Slack, email, and regular video calls. You\'ll have a dedicated project manager for daily updates and progress reports.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;